"use client";

import React, { useState, useMemo } from "react";
import { useLabels } from "@/lib/labels/LabelsProvider";
import { useTenantSettings } from "@/lib/tenant/TenantSettingsProvider";
import { useMatchPrediction } from "@/features/predictions/hooks/useMatchPrediction";
import { useTeamNextMatchForPredictor } from "@/features/predictions/hooks/useTeamNextMatchForPredictor";
import { useNavigation } from "@/lib/navigation/NavigationProvider";
import { SignInPromptModal } from "@/components/ui/modals/SignInPromptModal";
import { CTAView } from "./CTAView";
import { PredictedView } from "./PredictedView";
import { PredictionForm } from "./PredictionForm";
export const MatchPredictionCard: React.FC = () => {
  const {
    predictorLeaderboardId
  } = useTenantSettings();
  const labels = useLabels();
  const nav = useNavigation();
  const t = labels.matchPredictionCard;
  const predictionLabels = labels.prediction;
  const {
    nextMatch,
    existingPrediction,
    loading
  } = useTeamNextMatchForPredictor();

  // Fallback team objects for when nextMatch hasn't loaded yet (hooks must be called unconditionally)
  const homeTeam = nextMatch?.homeTeam ?? {
    name: "",
    shortName: ""
  } as any;
  const awayTeam = nextMatch?.awayTeam ?? {
    name: "",
    shortName: ""
  } as any;

  // --- Shared prediction logic via hook ---
  const {
    scores,
    isPredicted,
    submit,
    edit,
    isSubmitting,
    lastResult,
    isAuthenticated,
    oddsLoading,
    showSignInModal,
    setShowSignInModal,
    predictedOutcome
  } = useMatchPrediction({
    matchId: nextMatch?.id ?? "",
    homeTeam,
    awayTeam,
    kickoffAt: nextMatch?.kickoffAt ?? "",
    market: "CORRECT_SCORE",
    existingPrediction
  });

  // UI-only state
  const [showForm, setShowForm] = useState(false);

  // --- Format predicted outcome for PredictedView (same logic as PredictorMatchCard) ---
  const predictedOutcomeOdds = useMemo(() => {
    if (!predictedOutcome?.matchingOdd || !predictedOutcome.operator) return null;
    const {
      outcomeKey,
      matchingOdd,
      operator
    } = predictedOutcome;
    let label: string;
    if (outcomeKey === "1") {
      label = `${homeTeam.shortName || homeTeam.name} ${labels.predictorPage.odds.toWin}`;
    } else if (outcomeKey === "2") {
      label = `${awayTeam.shortName || awayTeam.name} ${labels.predictorPage.odds.toWin}`;
    } else {
      label = labels.predictorPage.odds.draw;
    }
    const deepLinkUrl = matchingOdd.url || operator.url;
    return {
      odds: [{
        label,
        value: matchingOdd.value,
        url: deepLinkUrl
      }],
      operator
    };
  }, [predictedOutcome, homeTeam, awayTeam, labels.predictorPage.odds]);

  // Hide if predictor is not configured, still loading, or no match found
  if (!predictorLeaderboardId || loading || !nextMatch) {
    return null;
  }

  // Build predictor page URL using the match's competition ID
  const predictorUrl = nav.predictor(nextMatch.context?.competition?.id);
  const matchDate = new Date(nextMatch.kickoffAt);

  // Background colors sourced from the gamification card style slot
  const gradientFrom = "var(--gamification-card-gradient-from)";
  const gradientTo = "var(--gamification-card-gradient-to)";
  const handleCTAClick = () => {
    if (!isAuthenticated) {
      setShowSignInModal(true);
      return;
    }
    setShowForm(true);
  };
  const handleEdit = () => {
    edit();
    setShowForm(true);
  };
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    e.stopPropagation();
    const result = await submit();
    if (result.success) {
      setShowForm(false);
    }
  };
  return <div className="w-full overflow-hidden relative gamification-card-surface" style={{
    ["--background-text-color" as any]: "var(--gamification-card-text)",
    ["--background-text-color-muted" as any]: "var(--gamification-card-text-muted)"
  }}>
            {/* Gradient overlay for text readability */}
            <div className="absolute inset-0 pointer-events-none" style={{
      background: "linear-gradient(to right, rgba(0,0,0,0.3) 0%, transparent 50%, rgba(0,0,0,0.3) 100%)"
    }} />

            <div className="relative z-10 p-6 sm:p-8 lg:p-10">
                {!showForm && !isPredicted ? <CTAView team={homeTeam} opponent={awayTeam} matchDate={matchDate} gradientFrom={gradientFrom} gradientTo={gradientTo} onCTAClick={handleCTAClick} labels={t} /> : !showForm && isPredicted ? <PredictedView team={homeTeam} opponent={awayTeam} homeScore={scores.home} awayScore={scores.away} onEdit={handleEdit} oddsLoading={oddsLoading} predictedOutcomeOdds={predictedOutcomeOdds} matchId={nextMatch.id} predictorUrl={predictorUrl} kickoffAt={nextMatch.kickoffAt} labels={{
        predictionSaved: predictionLabels.predictionSaved,
        edit: predictionLabels.edit,
        viewFullGame: t.viewFullGame
      }} /> : <PredictionForm team={homeTeam} opponent={awayTeam} homeScore={scores.home} awayScore={scores.away} isSubmitting={isSubmitting} hasError={!!lastResult && !lastResult.success} gradientFrom={gradientFrom} gradientTo={gradientTo} onIncrementScore={scores.increment} onDecrementScore={scores.decrement} onBack={() => setShowForm(false)} onSubmit={handleSubmit} labels={t} />}
            </div>

            <SignInPromptModal isOpen={showSignInModal} onClose={() => setShowSignInModal(false)} />
        </div>;
};